import { useEffect, useState } from 'react';

/**
 * Device-tier detection for the particle system. Coarse buckets keep the juice
 * from melting weak devices: a phone gets a fraction of the particle budget a
 * desktop does, and reduced-motion zeroes it entirely.
 *
 * Tiering is intentionally viewport-first (`<720px` → phone) because that is the
 * dimension that actually correlates with GPU fill-rate pain in this app (the
 * Hand screens on real phones), with CPU-core / device-memory hints demoting a
 * wide-but-weak machine from 'high' to 'low'.
 */

export type DeviceTier = 'high' | 'low' | 'phone';

export interface TierCaps {
  /** Max simultaneous live particles across every ambient layer on screen. */
  maxParticles: number;
  /** Backing-store resolution multiplier cap (canvas px = css px × this). */
  dprCap: number;
}

/** Frozen per-tier budgets. `reduced-motion` is handled separately (→ 0). */
export const TIER_CAPS: Record<DeviceTier, TierCaps> = {
  high: { maxParticles: 300, dprCap: 1.5 },
  low: { maxParticles: 150, dprCap: 1.5 },
  phone: { maxParticles: 40, dprCap: 1.0 },
};

/** Particle budget once reduced-motion is on — nothing renders. */
export const REDUCED_MOTION_CAP = 0;

const PHONE_MAX_WIDTH = 720;

function detectTier(): DeviceTier {
  if (typeof window === 'undefined') return 'low';

  const width = window.innerWidth;
  if (width < PHONE_MAX_WIDTH) return 'phone';

  // Wide viewport — decide high vs low from hardware hints. Both are optional
  // and unreliable, so treat a missing hint as "capable" and only demote on a
  // clearly weak signal.
  const cores = navigator.hardwareConcurrency ?? 8;
  const memory = (navigator as { deviceMemory?: number }).deviceMemory ?? 8;

  if (cores <= 4 || memory <= 4) return 'low';
  return 'high';
}

/**
 * Reactive device tier. Re-evaluates on resize (crossing the phone breakpoint —
 * e.g. a rotated tablet — flips the budget) and exposes the resolved caps.
 */
export function useDeviceTier(): { tier: DeviceTier; caps: TierCaps } {
  const [tier, setTier] = useState<DeviceTier>(detectTier);

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      // Debounce to a frame — resize fires in bursts during rotation/drag.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = detectTier();
        setTier((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return { tier, caps: TIER_CAPS[tier] };
}
